// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import {
  getBetTemplateFromProps,
  getDisplayLabelFromProps,
  getIsFromMultichoiceFromProps,
  getMatchIdFromProps,
  getNoLabelFromProps,
  getOutcomeIdOrOddsIdFromProps,
  getPathnameFromProps,
  getShowPercentDistribtionFromProps,
} from '../../selectors/_propsGetters_';
import {
  selectBasketIsCashout,
  selectBasketIsLoading,
  selectBasketItems,
  selectBasketMode,
  selectBasketStatus,
} from '../../selectors/basket/basketPrimary.selector';
import { selectBasketItemsMatchIds } from '../../selectors/basketItems.selector';
import {
  selectEntitiesBetsEntity,
  selectEntitiesBetsEntityAvailable,
} from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityAvailable,
  selectEntitiesMatchesEntityIsOutright,
  selectEntitiesMatchesEntitySportId,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOddsEntity } from '../../selectors/entities/odds/entitiesOddsPrimary.selector';
import {
  selectEntitiesOutcomesEntity,
  selectEntitiesOutcomesEntityAvailable,
  selectEntitiesOutcomesEntityHasOccured,
  selectEntitiesOutcomesEntityHotUsers,
  selectEntitiesOutcomesEntityVoidReason,
} from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesPlayers } from '../../selectors/entities/players/entitiesPlayersPrimary.selector';
import {
  makeIsAnyOfBasketMatchItemsCashoutingSelector,
  makeMatchCachedMMAPIResponsesInfosEntitySelector,
  selectMatchViewMode,
  makeNbBasketMatchSelectionsSelector,
  selectIsMatchEnded,
} from '../../selectors/match.selector';
import {
  selectMyMatchFutureEntitiesBetsEntityMmMultichoiceStatus,
  selectMyMatchFutureEntitiesBetsEntityMmStatus,
  selectMyMatchFutureEntitiesOutcomes,
  selectMyMatchFutureEntitiesOutcomesEntityMmContradicting,
  selectMyMatchFutureEntitiesOutcomesEntityMmOdds,
  selectMyMatchFutureEntitiesOutcomesEntityMultichoiceMmContradicting,
  selectMyMatchFutureEntitiesOutcomesEntityMultichoiceMmOdds,
} from '../../selectors/mymatch/mymatchPrimary.selector';
import { selectMyMatchFutureEntitiesOutcomesEntityAvailable } from '../../selectors/mymatch.selector';
import { MyMatchBetStatuses } from '../../states/mymatch';
import { BasketStatuses } from '../../types/basket';
import { BetTemplates } from '../../types/bet';
import { LabelConfigurations, type LabelConfiguration } from '../../types/betGroup';
import { CustomBetTemplates } from '../../types/betTemplate';
import { MatchViewModes, type MatchViewMode } from '../../types/matchView';
import { OutcomeModes, type OutcomeMode } from '../../types/outcome';
import { type SelectorMaker, type Selector } from '../../types/selectors';
import { type SportId } from '../../types/sports';
import BetStateUtils from '../../utils/bet';
import { getLabelConfiguration } from '../../utils/betTemplate';
import OddUtils from '../../utils/odd';
import { getIsBasketItemSelected } from '../../utils/outcome';
import isMatch from '../../utils/routes/isMatch';

type BetGroupOutcomePropsType = {
  sportId: SportId;
  flag?: string;
  title: string;
  value: number;
  mode: OutcomeMode;
  percentDistribution: number | null;
  selected: boolean;
  labelConfiguration: LabelConfiguration;
  isEnded: boolean;
  hasOccured?: boolean;
  voidReason?: number;
  matchViewMode?: MatchViewMode;
  futureBetIsSuspended?: boolean;
  displayMMOddsValue?: boolean;
};

const makeBetGroupOutcomePropsSelector: SelectorMaker<BetGroupOutcomePropsType> = () => {
  const selectFlag: Selector<string | undefined> = createSelector(
    [selectEntitiesOutcomesEntity, selectEntitiesMatchesEntity],
    (outcome, match) => (match?.isOutright ? outcome?.flag : undefined),
  );

  const selectTitle: Selector<string | undefined> = createSelector(
    [
      selectEntitiesOutcomesEntity,
      getBetTemplateFromProps,
      selectEntitiesBetsEntity,
      getDisplayLabelFromProps,
      selectEntitiesPlayers,
    ],
    (outcome, betTemplate, bet, displayLabel, players) => {
      let title = outcome?.label;
      if (betTemplate === BetTemplates.ASIAN_HANDICAP && title && displayLabel) {
        const splited = title.split(' ');
        title = splited[splited.length - 1];
      } else if (betTemplate === CustomBetTemplates.SOCCER_SCORERS_PERIOD) {
        const specifiers = BetStateUtils.specifiersToHash(bet?.specialBetValue);
        if (specifiers?.from !== null && specifiers?.to !== null) {
          title = `${specifiers?.from} - ${specifiers?.to}`;
        }
      } else if (betTemplate === CustomBetTemplates.SOCCER_PLAYER_PROPS && title) {
        const splited = title.split(' ');
        if (splited.length && splited[splited.length - 1].indexOf('+') !== -1) {
          title = splited[splited.length - 1];
        }
      } else if (betTemplate === CustomBetTemplates.HANDBALL_SCORERS_PLAYER) {
        const specifiers = BetStateUtils.specifiersToHash(bet?.specialBetValue);
        title = __('%1$s or more', specifiers?.goals);
      } else if (betTemplate === CustomBetTemplates.SOCCER_CARD_PLAYER) {
        const player = players?.[outcome?.playerId];

        if (player) {
          title = player.name;
        } else {
          // Par defaut, on supprime le 1+ du label de l'outcome
          title = title.replace(' 1+', '');
        }
      }
      return title;
    },
  );

  const selectPercentDistribution: Selector<number | null> = createSelector(
    [getShowPercentDistribtionFromProps, selectEntitiesOutcomesEntity],
    (showPercentDistribution, outcome) => (showPercentDistribution ? outcome?.percentDistribution || 0 : null),
  );

  const selectIsBasketItemSelected: Selector<boolean> = createSelector(
    [
      selectBasketMode,
      selectBasketStatus,
      selectBasketIsLoading,
      getOutcomeIdOrOddsIdFromProps,
      selectBasketItems,
      selectMatchViewMode,
      getIsFromMultichoiceFromProps,
      selectMyMatchFutureEntitiesBetsEntityMmStatus,
      selectMyMatchFutureEntitiesBetsEntityMmMultichoiceStatus,
    ],
    (
      basketMode,
      basketStatus,
      isLoading,
      oddsId,
      basketItems,
      matchViewMode,
      isFromMultichoice,
      betMMStatus,
      betMultichoiceMMStatus,
    ) =>
      getIsBasketItemSelected(
        basketMode,
        basketStatus,
        isLoading,
        oddsId,
        basketItems,
        matchViewMode,
        isFromMultichoice,
        betMMStatus,
        betMultichoiceMMStatus,
      ),
  );

  const selectLabelConfiguration: Selector<LabelConfiguration> = createSelector(
    [getBetTemplateFromProps, getNoLabelFromProps],
    (betTemplate, noLabel) => (!noLabel ? getLabelConfiguration(betTemplate) : LabelConfigurations.NO_LABEL),
  );

  const selectValue: Selector<number> = createSelector(
    [
      selectEntitiesOddsEntity,
      selectMyMatchFutureEntitiesOutcomesEntityAvailable,
      selectMyMatchFutureEntitiesOutcomesEntityMmOdds,
      selectMatchViewMode,
      selectBasketItems,
      selectMyMatchFutureEntitiesOutcomesEntityMultichoiceMmOdds,
      getMatchIdFromProps,
      getIsFromMultichoiceFromProps,
      selectBasketStatus,
    ],
    (
      oddsValue,
      mmAvailable,
      mmOdds,
      matchViewMode,
      basketItems,
      multichoiceMMOdds,
      matchId,
      isFromMultichoice,
      basketStatus,
    ) => {
      if (matchViewMode === MatchViewModes.MY_MATCH) {
        const currentMatchNbBasketItems = Object.keys(basketItems).reduce(
          (acc, itemId) => (basketItems[itemId].matchId === matchId ? acc + 1 : acc),
          0,
        );

        if (mmAvailable && currentMatchNbBasketItems && basketStatus !== BasketStatuses.VALIDATED) {
          if (isFromMultichoice) {
            return multichoiceMMOdds;
          }

          return mmOdds;
        }
      }

      return oddsValue;
    },
  );

  const selectFutureBetIsSuspended: Selector<boolean> = createSelector(
    [selectMyMatchFutureEntitiesBetsEntityMmStatus],
    (mmStatus) => mmStatus === MyMatchBetStatuses.SUSPENDED,
  );

  const selectDisplayMMOddsValue: Selector<boolean> = createSelector(
    [
      selectMyMatchFutureEntitiesOutcomes,
      selectBasketItemsMatchIds,
      getMatchIdFromProps,
      selectMatchViewMode,
      selectBasketStatus,
    ],
    (futureOutcomes, basketItemsMatchIds, matchId, matchViewMode, basketStatus) =>
      (matchViewMode === MatchViewModes.MY_MATCH &&
        (!!Object.keys(futureOutcomes).length || !~basketItemsMatchIds.indexOf(matchId))) ||
      basketStatus === BasketStatuses.VALIDATED,
  );

  const selectIsForbidden: Selector<boolean> = createSelector(
    [
      selectIsBasketItemSelected,
      selectBasketIsCashout,
      makeIsAnyOfBasketMatchItemsCashoutingSelector(),
      selectEntitiesMatchesEntityIsOutright,
    ],
    (selected, isCashout, isAnyOfBasketMatchItemsCashouting, isMatchOutright) => {
      return !selected && isCashout && isAnyOfBasketMatchItemsCashouting && !isMatchOutright;
    },
  );

  const selectMode: Selector<OutcomeMode> = createSelector(
    [
      getOutcomeIdOrOddsIdFromProps,
      selectEntitiesMatchesEntityAvailable,
      selectEntitiesBetsEntityAvailable,
      getBetTemplateFromProps,
      selectEntitiesOddsEntity,
      selectEntitiesOutcomesEntityAvailable,
      selectIsForbidden,
      selectMatchViewMode,
      selectMyMatchFutureEntitiesBetsEntityMmStatus,
      selectMyMatchFutureEntitiesBetsEntityMmMultichoiceStatus,
      selectMyMatchFutureEntitiesOutcomesEntityAvailable,
      selectMyMatchFutureEntitiesOutcomesEntityMmOdds,
      selectMyMatchFutureEntitiesOutcomesEntityMultichoiceMmOdds,
      makeMatchCachedMMAPIResponsesInfosEntitySelector(),
      selectMyMatchFutureEntitiesOutcomesEntityMmContradicting,
      selectMyMatchFutureEntitiesOutcomesEntityMultichoiceMmContradicting,
      getIsFromMultichoiceFromProps,
      makeNbBasketMatchSelectionsSelector(),
      selectIsBasketItemSelected,
    ],
    (
      oddsId,
      matchIsAvailable,
      betIsAvailable,
      betTemplate,
      oddValue,
      outcomeIsAvailable,
      isForbidden,
      matchViewMode,
      mmBetStatus,
      multichoiceMMBetStatus,
      mmOutcomeAvailable,
      mmOdds,
      multichoiceMMOdds,
      matchcachedMMAPIResponsesInfos,
      mmContradicting,
      multichoiceMMContradicting,
      isFromMultichoice,
      nbBasketMatchSelections,
      isSelected,
    ) =>
      oddsId
        ? OddUtils.getMode({
            oddsId,
            matchIsAvailable,
            betIsAvailable,
            betTemplate,
            oddValue,
            outcomeIsAvailable,
            isForbidden,
            matchViewMode,
            mmBetStatus,
            multichoiceMMBetStatus,
            mmOutcomeAvailable,
            mmOdds,
            multichoiceMMOdds,
            matchcachedMMAPIResponsesInfos,
            mmContradicting,
            multichoiceMMContradicting,
            isFromMultichoice,
            nbBasketMatchSelections,
            isSelected,
          })
        : OutcomeModes.STOP,
  );

  const selectHotNbUsers: Selector<number | undefined | null> = createSelector(
    [selectEntitiesOutcomesEntityHotUsers, getPathnameFromProps],
    (hotUsers, pathname) => (isMatch(pathname) ? hotUsers : null),
  );

  return createSelector(
    [
      selectEntitiesMatchesEntitySportId,
      selectFlag,
      selectTitle,
      selectValue,
      selectMode,
      selectPercentDistribution,
      selectIsBasketItemSelected,
      selectLabelConfiguration,
      selectIsMatchEnded,
      selectEntitiesOutcomesEntityHasOccured,
      selectEntitiesOutcomesEntityVoidReason,
      selectMatchViewMode,
      selectFutureBetIsSuspended,
      selectDisplayMMOddsValue,
      selectHotNbUsers,
    ],
    (
      sportId,
      flag,
      title,
      value,
      mode,
      percentDistribution,
      selected,
      labelConfiguration,
      isEnded,
      hasOccured,
      voidReason,
      matchViewMode,
      futureBetIsSuspended,
      displayMMOddsValue,
      hotNbUsers,
    ) => ({
      sportId,
      flag,
      title,
      value,
      mode,
      percentDistribution,
      selected,
      labelConfiguration,
      isEnded,
      hasOccured,
      voidReason,
      matchViewMode,
      futureBetIsSuspended,
      displayMMOddsValue,
      hotNbUsers,
    }),
  );
};

export default makeBetGroupOutcomePropsSelector;
